import React, { useState, useEffect, useMemo } from "react";
import GameDialog from "@nualang/nualang-ui-components/dist/Live/Game";
import ExerciseMenu from "@nualang/nualang-ui-components/dist/Dialogs/ExerciseMenu";
import useConfirm from "@nualang/nualang-ui-components/dist/hooks/useConfirm";
import { useQueryClient } from "@tanstack/react-query";
import {
  games as gameQuerys,
  users as userQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { getClassroom } from "@nualang/nualang-api-and-queries/APIs/Classrooms";
import { getUser } from "@nualang/nualang-api-and-queries/APIs/Users";
import { getClassroomMembers } from "@nualang/nualang-api-and-queries/APIs/ClassroomMembers";
import { classrooms as classroomQuerys } from "@nualang/nualang-api-and-queries/Queries";
import useExerciseState from "@nualang/nualang-ui-components/dist/hooks/useExerciseState";
import useClassroomLiveGame from "../../../../../hooks/useClassroomLiveGame";
import {
  classrooms as classroomHelpers,
  users as userHelpers,
} from "../../../../../utils/helpers";
import {
  DataType,
  invalidateCache,
} from "@nualang/nualang-api-and-queries/APIs/invalidation";
import Loading from "../../../../../components/Misc/Loading";
import { highlightSpeechText } from "../../../../../utils/index";
import useServices from "../../../../../hooks/useServices";
import useAudioPlayer from "../../../../../hooks/useAudioPlayer";
import config from "../../../../../config";
import logo from "../../../../../img/logo/nualang-logo-white.svg";
import ResponsiveDialog from "@nualang/nualang-ui-components/dist/Dialogs/ResponsiveDialog";
import useWebSocket from "react-use-websocket";
import { stopHighlightingSpeechText } from "../../../../../utils/index";
import { Toaster } from "react-hot-toast";

import logger from "../../../../../logger";
import { useApp } from "../../../../../hooks";

const { limits } = config;

function CloseGameMenu({
  open,
  close,
  t,
  gameStatus,
  cancelGame,
  closeGame,
  endGame,
  isButtonDisabled,
}) {
  return (
    <ExerciseMenu
      open={open && !isButtonDisabled}
      handleClose={close}
      actions={[
        ...(gameStatus === "CREATED"
          ? [
              {
                dataCypress: "cancel_game",
                text: t("cancel_game"),
                variant: "contained",
                color: "primary",
                onClick: cancelGame,
                disabled: isButtonDisabled,
              },
            ]
          : []),
        ...(gameStatus === "LIVE"
          ? [
              {
                dataCypress: "end_game",
                text: t("end_game"),
                variant: "contained",
                color: "primary",
                onClick: endGame,
                disabled: isButtonDisabled,
              },
            ]
          : [
              {
                dataCypress: "close_game",
                text: t("go_back_to_classroom"),
                variant: "",
                color: "",
                onClick: closeGame,
                disabled: isButtonDisabled,
              },
            ]),
      ]}
    />
  );
}

function CloseFinishedGame({
  t,
  gameStatus,
  closeGame,
  isButtonDisabled,
  gameUpdatedAt,
}) {
  const [isGameActive, setIsGameActive] = useState(true);

  useEffect(() => {
    if (Date.now() - gameUpdatedAt > 3000) {
      setIsGameActive(false);
    }
  }, [gameUpdatedAt]);

  return (
    <ResponsiveDialog
      open={
        !isGameActive &&
        ["CANCELED", "COMPLETED", "EXPIRED"].includes(gameStatus) &&
        !isButtonDisabled
      }
      isSubmitDisabled={isButtonDisabled}
      dialogTitle={t(
        gameStatus === "COMPLETED" ? "game_has_ended" : "game_has_canceled",
      )}
      submitText={t("go_back_to_classroom")}
      handleSubmit={closeGame}
      transitionDuration={{ enter: 0 }}
    />
  );
}

function PlayGame(props) {
  const { correctSound, incorrectSound, tadaSound, location } = props;
  const { t, i18n } = useTranslation();
  const params = useParams();
  const { gameId, classroomId } = params;
  const isCreator = location?.state?.isGameCreator;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tts, translate] = useServices();
  const audioPlayer = useAudioPlayer();
  const { addToQueue, stop, clearQueue } = audioPlayer;

  const {
    openSnackbar,
    startLoading,
    stopLoading,
    user,
    setUser,
    markCommunityAccessRestricted,
  } = useApp();
  const { username, attributes } = user;
  const [classroomMembersFormatted, setClassroomMembersFormatted] = useState(
    [],
  );
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [isLeaderboardVisible, setIsLeaderboardVisible] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isIconsVisible, setIsIconsVisible] = useState(false);
  const [finalPosition, setFinalPosition] = useState(0);
  const [gameAward, setGameAward] = useState("none");
  const [timeoutArray, setTimeoutArray] = useState([]);
  const [showRoleplayLeaderboardIcons, setShowRoleplayLeaderboardIcons] =
    useState(true);
  const [speechMarks, setSpeechMarks] = useState([]);

  const [confirm] = useConfirm(t);

  const leaveGame = async () => {
    const confirmed = await confirm(
      t("leave_game"),
      t("leave_game_confirmation"),
      t("leave"),
    );

    if (confirmed) {
      closeGame();
    }
  };

  const handleOpenCloseModal = () => {
    setIsCloseModalOpen(true);
  };

  const handleCloseCloseModal = () => {
    setIsCloseModalOpen(false);
  };

  const classroomQuery = classroomQuerys.useClassroom(
    async () => {
      const classroom = await getClassroom(classroomId);
      const formattedClassroom = await classroomHelpers.formatClassroom(
        classroom.Item,
        false,
        props.authData,
      );
      return formattedClassroom;
    },
    {
      classroomId,
      username,
    },
    { enabled: !!classroomId },
  );

  const classroom = useMemo(
    () =>
      classroomQuery.isSuccess &&
      classroomQuery.data &&
      classroomQuery.data.classroomId
        ? classroomQuery.data
        : {},
    [classroomQuery.data, classroomQuery.isSuccess],
  );

  // Fallback signal: this classroom may already be flagged by the backend as
  // community-access-restricted before the user's own record ever gets
  // flagged (see appAtoms.js). Learn about it as soon as we see it.
  useEffect(() => {
    markCommunityAccessRestricted([classroom]);
  }, [classroom, markCommunityAccessRestricted]);

  const isNualangLiveEnabled = true;

  const [selectedRoleplay, setSelectedRoleplay] = useState(null);
  const updateGameMemberMutation = gameQuerys.useUpdateGameMember();

  const updateUserMutation = userQuerys.useUpdateUser(
    userHelpers.updateUserAttributes,
    {
      onSuccess: async (data) => {
        setUser(data);
        queryClient.invalidateQueries({
          queryKey: userQuerys.userKeys.item(username),
        });
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );

  const handleUpdateUser = (values) => {
    updateUserMutation.mutate({ userId: username, values });
    queryClient.setQueryData(userQuerys.userKeys.item(username), (old) => {
      let prevState = old;
      let nextState = {
        ...prevState,
        gameAward: gameAward,
      };
      return nextState;
    });
  };

  const handleUpdateGameAward = async (medal) => {
    // eslint-disable-next-line react-hooks/purity
    const awardedAt = Date.now();
    const value = {
      gameAward: medal,
    };
    await handleUpdateUser(value);
    if (medal && medal !== "none") {
      await updateGameMemberMutation.mutateAsync({
        classroomId,
        gameId,
        memberId: username,
        values: {
          award: medal,
          classroomId,
          topicName: courseSectionTopic?.topicName ?? "",
          courseName: courseName ?? "",
          gameType: game?.exercise ?? "",
          awardedAt,
        },
      });
      queryClient.invalidateQueries({
        queryKey: gameQuerys.gameKeys.memberAwards(username),
      });
    }
  };

  const preferred_username = attributes.preferred_username;

  const {
    gameMemberIds,
    gameMembers,
    game,
    courseSectionTopic,
    courseName,
    courseQueryIsSuccess,
    activePhrases,
    currentQuestion,
    questions,
    isAnswered,
    useWordBank,
    listeningWords,
    listeningText,
    translationText,
    translationWords,
    readWriteText,
    isWordBankHidden,
    answerAttempt,
    isCorrectResultOpen,
    isIncorrectResultOpen,
    isPartiallyCorrectResultOpen,
    phraseWordList,
    time,
    isTimeUp,
    setIsAnswered,
    handleStartGame,
    handleCancelGame,
    handleEndGame,
    handleJoinLiveGame,
    handleLeaveLiveGame,
    handleNextQuestion,
    handleInputChange,
    handleUseWordBank,
    setListeningWords,
    setTranslationWords,
    setUseWordBank,
    checkListening,
    checkIsListeningCorrect,
    checkTranslation,
    checkMeaning,
    setIsTimeUp,
    handleTimeUp,
    gameMembersQuery,
    gameMembersHistory,
    wordBankEnabled,
    handleSetWordBankEnabled,
    handleSetTimer,
    allStudentsAnswered,
    setCurrentAnswer,
    answerOptions,
    setAnswerOptions,
    keyboardEnabled,
    handleChangeAnswerOptions,
    gameMembersAnsweredTotal,
    gameMembersCorrectAnswerTotal,
    isFinalScoreDrawerOpen,
    isRewardEarnedOpen,
    setIsRewardEarnedOpen,
    numberOfPhrases,
    numberOfQuestions,
    handleSetNumberOfQuestions,
    handleConfirm,
    confirmedNumberOfQuestions,
    classroomGamesQuery,
    currentAnswer,
    socketUrl,
    checkStory,
    wordOptions,
    restoreOrInitialiseTimerStart,
    gameModes,
    setGameModes,
    currentMessage,
    setCurrentMessage,
    handleUpdateGame,
    handleChangeGameModes,
    isAnswerAttemptCorrect,
    setIsCorrectResultOpen,
    setIsIncorrectResultOpen,
    isStudentAnswerDrawerOpen,
    setIsStudentAnswerDrawerOpen,
    roleplayAnswer,
    setRoleplayAnswer,
    handleUpdateActiveGameMember,
    answerCounts,
    noAnswerCounts,
    phraseListVoice,
    phraseListVoiceLanguageCode,
    phraseListVoicePitch,
    phraseListLearnLang,
  } = useClassroomLiveGame({
    classroomId,
    gameId,
    startLoading,
    stopLoading,
    openSnackbar,
    isNualangLiveEnabled,
    navigate,
    username,
    authData: props.authData,
    correctSound,
    incorrectSound,
    tadaSound,
    isClassroomCreator: classroom?.isCreator,
    context: "play-game",
    isIconsVisible,
    selectedRoleplay,
    handleUpdateGameAward,
    isCreator,
    preferred_username,
    phraseListId: location?.state?.phraseListId,
    phrases: location?.state?.phrases,
  });
  const [isGameMember, setIsGameMember] = useState(false);

  useEffect(() => {
    if (game?.roleplayId && courseSectionTopic?.roleplays?.length > 0) {
      setSelectedRoleplay(
        courseSectionTopic.roleplays.find(
          (rp) => rp.roleplayId === game.roleplayId,
        ),
      );
    }
  }, [game?.roleplayId, courseSectionTopic?.roleplays]);

  useEffect(() => {
    setIsGameMember(
      gameMembers.findIndex((gm) => gm.memberId === username) > -1
        ? true
        : false,
    );
  }, [gameMembers, username]);

  useEffect(() => {
    if (!isCreator && game?.gameId && !isGameMember) {
      handleJoinLiveGame(game?.gameId);
    }
  }, [game?.gameId, isCreator, isGameMember]);

  useEffect(() => {
    let timeoutId;

    if (game?.remainingQuestions?.length === 1 && answerAttempt && !isCreator) {
      timeoutId = setTimeout(() => {
        closeGame();
      }, 600000); // 10 minutes
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [game?.remainingQuestions?.length === 1, answerAttempt]);

  useEffect(() => {
    setFinalPosition(
      gameMembersHistory
        .slice(-1)
        .map(
          (members) => members.find((m) => m.memberId === username)?.index,
        )[0],
    );
  }, [gameMembersHistory]);

  useEffect(() => {
    let gameAward;
    if (finalPosition === 1) {
      gameAward = "gold";
    }
    if (finalPosition === 2) {
      gameAward = "silver";
    }
    if (finalPosition === 3) {
      gameAward = "bronze";
    }
    if (finalPosition > 3) {
      gameAward = "none";
    }
    setGameAward(gameAward);
    if (courseQueryIsSuccess && isFinalScoreDrawerOpen) {
      handleUpdateGameAward(gameAward);
    }
  }, [finalPosition, courseQueryIsSuccess, isFinalScoreDrawerOpen]);

  const classroomMembersQuery = classroomQuerys.useClassroomMembers(
    async (classroomId, filters) => {
      const members = await getClassroomMembers(classroomId, filters);
      return members;
    },
    {
      classroomId,
      username,
      filters: {
        limit: limits.classroomMembers,
      },
    },
    { enabled: isCreator && !!classroomId },
  );

  const classroomMembers = useMemo(
    () =>
      classroomMembersQuery.isSuccess &&
      Array.isArray(classroomMembersQuery.data.Items) &&
      classroomMembersQuery.data.Items.length
        ? classroomMembersQuery.data.Items
        : [],
    [classroomMembersQuery.data, classroomMembersQuery.isSuccess],
  );

  useEffect(() => {
    async function formatClassroomMembers() {
      if (classroomMembers.length) {
        const results = await classroomHelpers.formatMembers(
          classroomMembers || [],
        );
        setClassroomMembersFormatted(results);
      } else {
        setClassroomMembersFormatted([]);
      }
    }
    formatClassroomMembers();
  }, [classroomMembers]);

  const membersNotInRoom = classroomMembersFormatted.filter(
    (mem) => !gameMemberIds.includes(mem.memberId),
  );

  const handleClose = () => {
    if (isCreator) {
      handleOpenCloseModal();
      classroomGamesQuery.refetch();
    } else {
      leaveGame();
    }
  };

  const handleCloseRewardEarned = () => {
    setIsRewardEarnedOpen(false);
  };

  const cancelGame = () => {
    setIsButtonDisabled(true);
    handleCancelGame(() => {
      queryClient
        .invalidateQueries({
          queryKey: gameQuerys.gameKeys.lists(),
        })
        .catch(() => {
          setIsButtonDisabled(false);
        });
      navigate(`/classrooms/${classroomId}`);
    });
  };

  const endGame = () => {
    setIsButtonDisabled(true);
    handleEndGame(() => {
      queryClient
        .invalidateQueries({
          queryKey: gameQuerys.gameKeys.lists(),
        })
        .catch(() => {
          setIsButtonDisabled(false);
        });
      navigate(`/classrooms/${classroomId}`);
    });
  };

  const closeGame = async () => {
    if (isCreator) {
      navigate(`/classrooms/${classroomId}`);
    } else if (game?.gameId && game?.gameStatus === "CREATED" && !isCreator) {
      handleLeaveLiveGame(gameId, () => {
        navigate(`/classrooms/${classroomId}`);
      });
    } else {
      if (game?.remainingQuestions?.length !== 1) {
        handleUpdateGameAward(gameAward);
      }
      await handleUpdateActiveGameMember(gameId, false);
      navigate(`/classrooms/${classroomId}`);
    }
  };

  const handleSpeak = async (params) => {
    try {
      const {
        text,
        learnLanguage,
        voiceName,
        mute,
        callback,
        textContainer,
        languageCode,
        voiceSpeed,
        pitch,
      } = params;
      const result = await tts(
        text,
        learnLanguage,
        voiceName,
        languageCode,
        voiceSpeed,
        pitch,
      );
      if (!mute && result && result.audioUrl) {
        setSpeechMarks(result.speechMarks || []);
        const highlightCallback = () =>
          highlightSpeechText(
            textContainer,
            result.speechMarks,
            setTimeoutArray,
          );
        addToQueue(result.audioUrl, callback, highlightCallback);
      } else if (result.errorMessage) {
        throw new Error(result.errorMessage);
      }
      return result;
    } catch (error) {
      logger.error(error);
      return null;
    }
  };

  const handleStopSpeaking = async (textContainer) => {
    try {
      stopHighlightingSpeechText(textContainer, timeoutArray);
      stop();
      clearQueue();
    } catch (error) {
      logger.error(error);
    }
  };

  const [wordBankTooltipOpen, setWordBankTooltipOpen] = useState(false);
  const initialVoiceSpeed =
    courseSectionTopic && courseSectionTopic.voiceSpeed
      ? courseSectionTopic.voiceSpeed
      : "";
  const phrases = activePhrases ?? courseSectionTopic.phrases ?? [];
  const learnLang = phraseListLearnLang || courseSectionTopic.learnLang;

  const exerciseState = useExerciseState({
    open: true,
    handleClose,
    exerciseName: game.exercise,
    phrases: phrases,
    initialVoiceSpeed,
    remainingQuestions: game.remainingQuestions,
    existingAnswerAttempts: null,
    exerciseStartTime: null,
    handleSaveProgress: () => {},
    handleDeleteProgress: null,
    openSnackbar,
    courseId: null,
    sectionId: null,
    topicId: null,
    history: null,
    handleSpeak,
    learnLang: learnLang,
    hiddenWordBankExercises: [],
    hiddenImageExercises: [],
    correctSound: null,
    incorrectSound: null,
    disableShuffle: null,
    toggleMuteSound: null,
    isLiveGame: true,
    roleplay: selectedRoleplay,
    gameId: "story",
    handleStopSpeaking,
    stopAudio: stop,
    existingMessages: game.roleplayMessages,
    isCreator,
  });
  const {
    ttsResult,
    isSpeaking,
    voiceSpeed,
    isVoiceSpeedMenuOpen,
    pollyResult,
    speak,
    handleOpenVoiceSpeedMenu,
    handleCloseVoiceSpeedMenu,
    handleChangeVoiceSpeed,
    isSpeakingTextContainer,
    stopSpeaking,
    handleNext,
    handleCorrectQuestionAnswer,
    handleIncorrectQuestionAnswer,
    muteSound,
    answerAttempts,
    startRoleplay,
    roleplayHasStarted,
    roleplayMessages,
    isCompletionModalOpen,
  } = exerciseState;

  const { sendJsonMessage } = useWebSocket(socketUrl, {
    onMessage: (e) => {
      const data = e.data ? JSON.parse(e.data) : {};
      if (data.action === "MESSAGE") {
        if (
          data.payload.message === "NEXT_MESSAGE" &&
          data.payload.username !== username
        ) {
          handleNext();
        } else if (
          data.payload.message === "SHOW_STORY_ANSWER_RESULT" &&
          data.payload.username !== username
        ) {
          if (isAnswerAttemptCorrect) {
            setIsCorrectResultOpen(true);
          } else {
            setIsIncorrectResultOpen(true);
          }
        } else if (
          data.payload.message === "JOINED_GAME" ||
          data.payload.message === "LEFT_GAME"
        ) {
          setTimeout(async () => {
            await invalidateCache(DataType.GAME_MEMBERS, {
              classroomId: classroomId,
              gameId: gameId,
            });
            gameMembersQuery.refetch();
          }, 2000);
        }
      }
    },
  });

  const handleShowLeaderboard = async (nextScriptLine) => {
    if (isIconsVisible === true) {
      setIsIconsVisible(false);
    }
    setIsLeaderboardVisible(true);
    if (game?.exercise?.includes("roleplay") && nextScriptLine !== false) {
      sendJsonMessage({
        action: "MESSAGE",
        payload: {
          classroomId,
          gameId,
          username,
          message: "SHOW_STORY_ANSWER_RESULT",
        },
      });
    }
    setIsAnswered(false);
    const gameContainer = document.getElementById("gameContainer");
    gameContainer.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    setTimeout(async () => {
      await invalidateCache(DataType.GAME_MEMBERS, {
        classroomId: classroomId,
        gameId: gameId,
      });
      await gameMembersQuery.refetch();
      setIsIconsVisible(true);
    }, 2000);
  };

  const handleRoleplayNext = (nextScriptLine) => {
    if (nextScriptLine) {
      sendJsonMessage({
        action: "MESSAGE",
        payload: {
          classroomId,
          gameId,
          username,
          message: "NEXT_MESSAGE",
        },
      });
      handleNext();
    } else {
      handleShowLeaderboard(false);
      sendJsonMessage({
        action: "MESSAGE",
        payload: {
          classroomId,
          gameId,
          username,
          message: "SHOW_FINAL_SCORES",
        },
      });
    }
  };

  useEffect(() => {
    if (game?.exercise?.includes("roleplay")) {
      restoreOrInitialiseTimerStart(
        gameId ? `${gameId}-roleplay-${roleplayMessages?.length ?? 0}` : null,
      );
    }
    if (isCreator) {
      handleUpdateGame({
        roleplayMessages: roleplayMessages,
      });
    }
    if (roleplayMessages?.length > 0) {
      setCurrentMessage(roleplayMessages[roleplayMessages.length - 1]);
    }
  }, [roleplayMessages]);

  useEffect(() => {
    if (game?.exercise?.includes("roleplay") && selectedRoleplay) {
      const index = selectedRoleplay.script.findIndex((scriptLine) =>
        Object.keys(scriptLine).includes("question"),
      );
      if (roleplayMessages?.length - 1 > index) {
        setShowRoleplayLeaderboardIcons(true);
      } else {
        setShowRoleplayLeaderboardIcons(false);
      }
    }
  }, [game, selectedRoleplay, roleplayMessages]);

  const selectedSentence =
    game?.exercise === "listening"
      ? listeningWords.map((word) => word.text).join(" ")
      : translationWords.map((word) => word.text).join(" ");

  const voices = (() => {
    if (phraseListVoiceLanguageCode) {
      const langConfig = Object.values(config.languages).find(
        (lang) => lang.Voices?.[phraseListVoiceLanguageCode],
      );
      if (langConfig) return langConfig.Voices;
    }
    return learnLang
      ? config.languages[learnLang.toLowerCase()]?.Voices || []
      : [];
  })();

  const firstRegion = voices ? Object.values(voices)[0] : null;
  const voice = phraseListVoice || firstRegion?.voiceOptions?.[0]?.name || null;
  const voiceLanguageCode = phraseListVoiceLanguageCode || firstRegion?.languageCode || null;
  const voicePitch = phraseListVoicePitch;

  const handleCheckStory = (committedAnswer) => {
    if (currentMessage) {
      const answerToCheck = committedAnswer ?? currentAnswer;
      const selectedAnswer = currentMessage?.answers.find(
        (answer) => answer.text === answerToCheck.text,
      );
      const isCorrect = selectedAnswer?.correct;
      let answerAttempt = {
        attempt: answerToCheck,
        answer: currentMessage?.answers.filter((answer) => answer.correct),
        result: isCorrect ? "correct" : "incorrect",
        method: null,
      };
      checkStory(isCorrect, answerAttempt);
    }
  };

  const handleContinue = (committedAnswer) => {
    if (game?.exercise === "listening") {
      return checkListening();
    } else if (game?.exercise === "translation") {
      return checkTranslation();
    } else if (game?.exercise === "meaning" || game?.exercise === "meaning-read-and-write") {
      return checkMeaning();
    } else if (selectedRoleplay) {
      return handleCheckStory(committedAnswer);
    }
  };

  let numOfQuestions, numOfCorrectAnswers, completed;
  if (currentQuestion) {
    numOfQuestions =
      confirmedNumberOfQuestions > 0
        ? confirmedNumberOfQuestions
        : phrases.length;
    numOfCorrectAnswers = numOfQuestions + 1 - questions.length;
    completed = (numOfCorrectAnswers / numOfQuestions) * 100;
  }
  if (selectedRoleplay) {
    numOfQuestions = selectedRoleplay.script.length;
    numOfCorrectAnswers = roleplayMessages?.length;
    completed = (numOfCorrectAnswers / numOfQuestions) * 100;
  }

  const handleNextExercise = () => {
    setIsLeaderboardVisible(false);
    setIsIconsVisible(false);
    handleNextQuestion();
  };

  const userQuery = userQuerys.useUser(
    async (userId) => {
      const response = await getUser(userId, false);
      return response;
    },
    {
      userId: username,
    },
    {
      enabled: !!username,
    },
  );

  const currentUser =
    userQuery.isSuccess && userQuery.data ? userQuery.data : {};

  if (!game?.gameId || !courseSectionTopic?.topicId) {
    return <Loading />;
  }
  return (
    <>
      <GameDialog
        open={true}
        t={t}
        handleClose={handleClose}
        membersNotInRoom={membersNotInRoom}
        membersInRoom={gameMembers}
        exerciseName={game.exercise}
        topicName={courseSectionTopic.topicName}
        sources={location?.state?.phraseLists || game?.phraseLists || undefined}
        classroomName={classroom?.classroomName}
        classroomURL={`${window.location.origin}/classrooms/${classroomId}`}
        handleUpdateGame={handleStartGame}
        isCreator={isCreator}
        gameStatus={game.gameStatus}
        exercise={game.exercise}
        username={attributes.preferred_username}
        memberId={attributes.username}
        userImage={attributes.picture}
        gameAward={currentUser?.gameAward}
        handleSpeak={handleSpeak}
        currentQuestion={currentQuestion}
        isLeaderboardVisible={isLeaderboardVisible}
        topicInfo={courseSectionTopic}
        setIsLeaderboardVisible={setIsLeaderboardVisible}
        voices={voices}
        remainingQuestions={game.remainingQuestions}
        endGame={endGame}
        languageInformation={
          courseSectionTopic.learnLang
            ? config.languages[
                courseSectionTopic.learnLang.toLowerCase() || "english"
              ]
            : null
        }
        learnLang={courseSectionTopic?.learnLang}
        learnLangCharacters={
          courseSectionTopic?.learnLang
            ? config.languages[
                courseSectionTopic?.learnLang.toLowerCase() || "english"
              ].characters
            : []
        }
        languageTag={
          courseSectionTopic.learnLang
            ? config.languages[
                courseSectionTopic.learnLang.toLowerCase() || "english"
              ].Tag.split("-")[0]
            : "en"
        }
        siteLanguage={i18n.language}
        wordBankTooltipOpen={wordBankTooltipOpen}
        setWordBankTooltipOpen={setWordBankTooltipOpen}
        selectedSentence={selectedSentence}
        isTimeUp={isTimeUp}
        setIsTimeUp={setIsTimeUp}
        time={time}
        handleTimeUp={handleTimeUp}
        handleNextExercise={handleNextExercise}
        questions={questions}
        wordOptions={wordOptions}
        listeningText={listeningText}
        translationText={translationText}
        readWriteText={readWriteText}
        ttsResult={ttsResult}
        listeningWords={listeningWords}
        translationWords={translationWords}
        useWordBank={useWordBank}
        isWordBankHidden={isWordBankHidden}
        isSpeaking={isSpeaking}
        answerAttempt={answerAttempt}
        isCorrectResultOpen={isCorrectResultOpen}
        isIncorrectResultOpen={isIncorrectResultOpen}
        isPartiallyCorrectResultOpen={isPartiallyCorrectResultOpen}
        phraseWordList={phraseWordList}
        checkIsListeningCorrect={checkIsListeningCorrect}
        handleInputChange={handleInputChange}
        setListeningWords={setListeningWords}
        setTranslationWords={setTranslationWords}
        setUseWordBank={setUseWordBank}
        handleUseWordBank={handleUseWordBank}
        handleContinue={handleContinue}
        handleNextQuestion={handleNextQuestion}
        pollyResult={pollyResult}
        speak={speak}
        numOfQuestions={numOfQuestions}
        numOfCorrectAnswers={numOfCorrectAnswers}
        completed={completed}
        isAnswered={isAnswered}
        setIsAnswered={setIsAnswered}
        voice={voice}
        voicePitch={voicePitch}
        voiceLanguageCode={voiceLanguageCode}
        logoSrc={logo}
        voiceSpeed={voiceSpeed}
        isVoiceSpeedMenuOpen={isVoiceSpeedMenuOpen}
        handleOpenVoiceSpeedMenu={handleOpenVoiceSpeedMenu}
        handleCloseVoiceSpeedMenu={handleCloseVoiceSpeedMenu}
        handleChangeVoiceSpeed={handleChangeVoiceSpeed}
        handleShowLeaderboard={handleShowLeaderboard}
        gameMembersHistory={gameMembersHistory}
        wordBankEnabled={wordBankEnabled}
        handleSetWordBankEnabled={handleSetWordBankEnabled}
        handleSetTimer={handleSetTimer}
        allStudentsAnswered={allStudentsAnswered}
        setCurrentAnswer={setCurrentAnswer}
        answerOptions={answerOptions}
        setAnswerOptions={setAnswerOptions}
        keyboardEnabled={keyboardEnabled}
        handleChangeAnswerOptions={handleChangeAnswerOptions}
        gameMembersAnsweredTotal={gameMembersAnsweredTotal}
        gameMembersCorrectAnswerTotal={gameMembersCorrectAnswerTotal}
        isFinalScoreDrawerOpen={isFinalScoreDrawerOpen}
        isRewardEarnedOpen={isRewardEarnedOpen}
        isIconsVisible={isIconsVisible}
        showRoleplayLeaderboardIcons={showRoleplayLeaderboardIcons}
        numberOfPhrases={numberOfPhrases}
        numberOfQuestions={numberOfQuestions}
        handleSetNumberOfQuestions={handleSetNumberOfQuestions}
        handleConfirm={handleConfirm}
        selectedRoleplay={selectedRoleplay}
        isSpeakingTextContainer={isSpeakingTextContainer}
        stopSpeaking={stopSpeaking}
        handleNext={handleRoleplayNext}
        handleCorrectQuestionAnswer={handleCorrectQuestionAnswer}
        handleIncorrectQuestionAnswer={handleIncorrectQuestionAnswer}
        muteSound={muteSound}
        answerAttempts={answerAttempts}
        startRoleplay={startRoleplay}
        roleplayHasStarted={roleplayHasStarted}
        roleplayMessages={roleplayMessages}
        isCompletionModalOpen={isCompletionModalOpen}
        gameModes={gameModes}
        setGameModes={setGameModes}
        currentMessage={currentMessage}
        handleChangeGameModes={handleChangeGameModes}
        handleTranslate={translate}
        handleCloseRewardEarned={handleCloseRewardEarned}
        isStudentAnswerDrawerOpen={isStudentAnswerDrawerOpen}
        setIsStudentAnswerDrawerOpen={setIsStudentAnswerDrawerOpen}
        roleplayAnswer={roleplayAnswer}
        setRoleplayAnswer={setRoleplayAnswer}
        answerCounts={answerCounts}
        noAnswerCounts={noAnswerCounts}
        setIsIconsVisible={setIsIconsVisible}
        speechMarks={speechMarks}
      />

      <CloseGameMenu
        t={t}
        open={isCloseModalOpen}
        close={handleCloseCloseModal}
        cancelGame={cancelGame}
        closeGame={closeGame}
        endGame={endGame}
        gameStatus={game.gameStatus}
        isButtonDisabled={isButtonDisabled}
      />
      <CloseFinishedGame
        t={t}
        closeGame={closeGame}
        gameStatus={game.gameStatus}
        gameUpdatedAt={game.updatedAt}
        isButtonDisabled={isButtonDisabled}
      />
      <Toaster position="bottom-left" reverseOrder={true} />
    </>
  );
}

export default PlayGame;
